<template>
  <div class="tab-content-inner">
    <!-- Search and Filters Section -->
    <div class="filters-section">
      <div class="search-container">
        <label class="search-label">{{ $t("globals.search") }}</label>
        <b-form-input
          v-model="searchQuery.customerName"
          @keyup.enter="selectFetch"
          class="search-input"
          :placeholder="$t('globals.customer') + '...'"
          :disabled="getAllBillingInvoicesLoading"
        />
      </div>

      <div class="filter-group">
        <div class="filter-item">
          <label class="filter-label">{{ $t("globals.invoice_no") }}</label>
          <b-form-input
            v-model="searchQuery.invoiceNo"
            @keyup.enter="selectFetch"
            class="filter-select"
            :disabled="getAllBillingInvoicesLoading"
          />
        </div>

        <div class="filter-item">
          <label class="filter-label">{{ $t("globals.sent_from") }}</label>
          <b-form-datepicker
            v-model="searchQuery.sentFrom"
            class="filter-select"
            :disabled="getAllBillingInvoicesLoading"
          ></b-form-datepicker>
        </div>

        <div class="filter-item">
          <label class="filter-label">{{ $t("globals.sent_to") }}</label>
          <b-form-datepicker
            v-model="searchQuery.sentTo"
            class="filter-select"
            :min="searchQuery.sentFrom ? new Date(searchQuery.sentFrom) : null"
            :disabled="getAllBillingInvoicesLoading"
          ></b-form-datepicker>
        </div>
      </div>

      <div class="action-buttons">
        <b-button
          variant="primary"
          class="search-btn"
          @click="selectFetch"
        >
          {{ $t("globals.search").toUpperCase() }}
        </b-button>
        <button
          class="reload-btn"
          @click="resetReload"
          :disabled="getAllBillingInvoicesLoading"
          v-b-tooltip.hover
          :title="$t('globals.clear_filters')"
        >
          <icon name="sync-alt" :spin="getAllBillingInvoicesLoading"></icon>
        </button>
      </div>
    </div>

    <!-- Table -->
    <div class="table-wrapper" style="position: relative; min-height: 300px;">
      <!-- Loading Overlay -->
      <div v-if="getAllBillingInvoicesLoading" class="loading-overlay">
        <div class="spinner"></div>
        <p>{{ $t("globals.loading") }}...</p>
      </div>

      <!-- Table Content -->
      <table class="modern-table">
        <thead>
          <tr>
            <th
              v-for="field in fields"
              :key="field.key"
              :class="{ sortable: field.sortable, [field.class]: field.class }"
              @click="field.sortable && handleSort(field.key)"
            >
              {{ field.label || field.key }}
              <span v-if="sortBy === field.key" class="sort-icon">
                {{ sortDesc ? '▼' : '▲' }}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <!-- Empty State Row -->
          <tr v-if="!getAllBillingInvoicesLoading && getAllBillingInvoices.length === 0">
            <td :colspan="fields.length" class="empty-state-cell">
              {{ $t("globals.no_results") }}
            </td>
          </tr>

          <!-- Data Rows -->
          <tr
            v-for="item in getAllBillingInvoices"
            :key="item.id"
            class="table-row"
            @click="rowClicked(item)"
          >
            <td>{{ item.id }}</td>
            <td>
              <a href="#" class="customer-link" @click.stop="openPdf(item.id)">
                {{ item.invoice_no }}
              </a>
            </td>
            <td>
              {{ item.credit_invoice_no }}
              <span v-if="item.is_credit_invoice" class="status-badge status-cancelled ml-1">
                {{ $t('globals.credit') }}
              </span>
            </td>
            <td>
              <span
                v-if="item.sent_at"
                class="status-badge status-confirmed"
                v-b-tooltip.hover
                :title="item.sent_at + '\n' + item.sent_to"
              >
                {{ item.sent_at.substring(0, 10) }}
              </span>
            </td>
            <td>
              <span
                v-if="item.re_sent_at"
                class="status-badge status-pending"
                v-b-tooltip.hover
                :title="item.re_sent_at"
              >
                {{ item.re_sent_at.substring(0, 10) }}
              </span>
            </td>
            <td>
              <span v-if="item.paid_at" class="status-badge status-completed">
                {{ $t('globals.paid') }}
              </span>
            </td>
            <td style="text-align: right;">{{ currencyFormat(item.total_price, item.currency) }}</td>
            <td style="text-align: right;">{{ currencyFormat(item.total_price_ex_vat, item.currency) }}</td>
            <td>
              <router-link
                v-if="item.customer"
                class="customer-link"
                :to="{ name: 'customer', params: { id: item.customer.id } }"
                @click.native.stop
                target="_blank"
              >
                {{ item.customer.company_name }}
              </router-link>
              <span v-else>-</span>
            </td>
            <td>
              <router-link
                v-if="item.order"
                class="customer-link"
                :to="{ name: 'order', params: { id: item.order.id } }"
                @click.native.stop
                target="_blank"
              >
                {{ item.order.order_no }}
              </router-link>
              <span v-else>-</span>
            </td>
            <td v-b-tooltip.hover :title="item.internal_notes">
              {{
                item.internal_notes && item.internal_notes.length > 15
                  ? item.internal_notes.substring(0, 15) + "..."
                  : item.internal_notes || '-'
              }}
            </td>
            <td v-b-tooltip.hover :title="item.created_at" style="text-align: right;">
              {{ item.created_at ? item.created_at.substring(0, 10) : '-' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div class="pagination-section">
      <div class="controls-left">
        <span class="summary-label">{{ $t("globals.showing") }} {{ offset + 1 }}-{{ Math.min(offset + limit, offset + getAllBillingInvoices.length) }}</span>
      </div>
      <div class="pagination-controls">
        <button
          class="page-btn"
          :disabled="offset === 0 || getAllBillingInvoicesLoading"
          @click="fetchLess"
        >
          &lt;
        </button>
        <button
          class="page-btn"
          :disabled="getAllBillingInvoices.length < limit || getAllBillingInvoicesLoading"
          @click="fetchMore"
        >
          &gt;
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { currencyFormat } from "@/helpers/price";
import { mapGetters, mapActions } from "vuex";

import "vue-awesome/icons/sync-alt";

export default {
  components: {
    Icon: () => import("vue-awesome/components/Icon.vue"),
  },
  data() {
    return {
      showNoteModal: false,
      notes: "",
      invoice_notes_id: null,
      offset: 0,
      limit: 20,
      sortBy: "created_at",
      sortDesc: true,
      searchQuery: {
        invoiceNo: null,
        customerName: null,
        sentFrom: null,
        sentTo: null,
      },
      fields: [
        {
          key: "id",
          sortable: true,
        },
        {
          key: "invoice_no",
          label: this.$t("globals.invoice_no"),
          sortable: true,
        },
        {
          key: "credit_invoice_no",
          label: this.$t("globals.type"),
          sortable: true,
        },
        {
          key: "sent_at",
          label: this.$t("globals.sent_at"),
          sortable: true,
        },
        {
          key: "re_sent_at",
          label: this.$t("globals.re_sent_at"),
          sortable: true,
        },
        {
          key: "paid_at",
          label: this.$t("globals.paid"),
          sortable: true,
        },
        {
          key: "total_price",
          label: this.$t("globals.total"),
          class: "text-right",
          sortable: true,
        },
        {
          key: "total_price_ex_vat",
          label: this.$t("globals.total_price_no_vat"),
          class: "text-right",
          sortable: true,
        },
        {
          key: "customer",
          label: this.$t("globals.customer"),
          sortable: true,
        },
        {
          key: "order",
          label: this.$t("globals.order_no"),
          sortable: true,
        },
        {
          key: "internal_notes",
          label: this.$t("globals.internal_notes"),
          sortable: true,
        },
        {
          key: "created_at",
          label: this.$t("globals.created_at"),
          class: "text-right",
          sortable: true,
        },
      ],
    };
  },
  methods: {
    ...mapActions("axios_mod", ["simpleMutation"]),
    ...mapActions("invoices", [
      "fetchBillingInvoicesPagination",
      "fetchBillingInvoicesLikeInvoiceNo",
      "fetchBillingInvoicesBySentAtRange",
      "fetchBillingInvoicesLikeCustomerName"
    ]),
    currencyFormat,
    rowClicked(item) {
      // Don't navigate if user is selecting text
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) {
        return;
      }
      this.$router.push({ name: "invoice", params: { id: item.id } });
    },
    openPdf(id) {
      window.open(
        `${this.getBackendUrl}/pdf_invoices/${id}.pdf?tmpl=0&locale=${this.getLocale}`,
        "_blank"
      );
    },
    handleSort(fieldKey) {
      if (this.sortBy === fieldKey) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortBy = fieldKey;
        this.sortDesc = false;
      }
      this.fetch();
    },
    fetchMore() {
      this.offset += 20;
      this.fetch();
    },
    fetchLess() {
      if (this.offset >= 20) {
        this.offset -= 20;
        this.fetch();
      }
    },
    resetOffsetSort() {
      this.offset = 0;
      this.sortBy = "created_at";
      this.sortDesc = true;
    },
    resetQuery() {
      this.searchQuery.invoiceNo = null;
      this.searchQuery.customerName = null;
      this.searchQuery.sentFrom = null;
      this.searchQuery.sentTo = null;
    },
    fetch() {
      let order_by = this.sortBy;
      // Customer exception
      if (order_by === "customer") {
        order_by = "customer_id";
      } else if (order_by === "order") {
        order_by = "order_id";
      }

      if (this.sortDesc) {
        order_by += " DESC";
      } else {
        order_by += " ASC";
      }

      this.fetchBillingInvoicesPagination({
        this_ref: this,
        limit: this.limit,
        offset: this.offset,
        order: order_by,
      });
    },
    selectFetch() {
      if (
        this.searchQuery.invoiceNo != null
        && this.searchQuery.invoiceNo.trim().length > 0
      ) {
        this.searchBillingInvoicesLikeInvoiceNo();
      } else if (
        this.searchQuery.customerName != null
        && this.searchQuery.customerName.trim().length > 2
      ) {
        this.searchBillingInvoicesLikeCustomerName();
      } else if (
        this.searchQuery.sentFrom != null
        || this.searchQuery.sentTo != null
      ) {
        this.searchBillingInvoicesBySentAtRange();
      } else {
        this.fetch();
      }
    },
    searchBillingInvoicesLikeInvoiceNo() {
      if (this.searchQuery.invoiceNo != null && this.searchQuery.invoiceNo.length > 2) {
        this.searchQuery.customerName = null;
        this.searchQuery.sentFrom = null;
        this.searchQuery.sentTo = null;

        this.fetchBillingInvoicesLikeInvoiceNo({
          this_ref: this,
          invoice_no: this.searchQuery.invoiceNo
        });
      }
    },
    searchBillingInvoicesLikeCustomerName() {
      if (this.searchQuery.customerName != null && this.searchQuery.customerName.length > 2) {
        this.searchQuery.invoiceNo = null;
        this.searchQuery.sentFrom = null;
        this.searchQuery.sentTo = null;

        this.fetchBillingInvoicesLikeCustomerName({
          this_ref: this,
          customer_name: this.searchQuery.customerName
        });
      }
    },
    searchBillingInvoicesBySentAtRange() {
      this.searchQuery.invoiceNo = null;
      this.searchQuery.customerName = null;

      this.fetchBillingInvoicesBySentAtRange({
        this_ref: this,
        sent_from: this.searchQuery.sentFrom,
        sent_to: this.searchQuery.sentTo
      });
    },
    resetReload() {
      this.resetOffsetSort();
      this.resetQuery();
      this.fetch();
    },
  },
  computed: {
    ...mapGetters("invoices", [
      "getAllBillingInvoices",
      "getAllBillingInvoicesLoading",
    ]),
    ...mapGetters(["getBackendUrl", "getLocale", "getUserBrandPermissions"]),
  },
  watch: {
    "searchQuery.sentFrom": function(val) {
      if (val) {
        this.searchBillingInvoicesBySentAtRange();
      }
    },
    "searchQuery.sentTo": function(val) {
      if (val) {
        this.searchBillingInvoicesBySentAtRange();
      }
    },
  },
  mounted() {
    this.fetch();
  },
};
</script>

<style scoped>
/* Styles are imported from modern-table.css */
</style>
